// @givmo/board — EVERY word officer roles LAYER 3 adds, in one place: what each title does in Givmo
// (the duties half of Governance › Officers, the member page's lines, whom Board asks to sign the
// minutes and the suggestion that follows from it), and the holdover's line on the Officers page
// (CBG-YR3-ROLES layer 3 and CBG-YR3-ROLES-HOLDOVER, D-CBG-21; backend contract
// `board-officer-roles.md` §14 and §15).
//
// ══ WHICH WORDS ARE APPROVED ════════════════════════════════════════════════════════════════════
//
// All of them. Jonathan approved the layer-3 frames for building on 2026-10-07 ("Approved") with
// every word kept a draft, and then the words themselves in copy round CBG-YR3-ROLES-COPY3 on
// 2026-10-13 09:30 PDT ("yes"), every line on the round's page as built, "term ended – still
// serving" included; "How this works"'s second line changed in CBG-FIX104 (2026-10-17 17:42 PDT,
// "1 and 2, approved."), where it sits. Each line names the frame that draws it (`l3-…`), or "not
// drawn" for a state no frame shows (written in the frames' voice).
//
// ⚠ Plan §3.2 and §3.4 bind every line: a duty is one of Board's own acts the board tied to a title,
// never what the law asks of an office — "what this title does in Givmo", never "must" or "required";
// a duty decides whom Board asks and what a dialog suggests, never who may act. No pronoun for a named
// person.

export const officerRolesL3Words = {
  // ── The duties: Board's own acts, in the catalog's order (§14.2) ───────────────────────────────

  // Drawn, l3-duties-start, l3-duties and l3-duties-edit.
  duty: {
    presides: 'Presides at meetings',
    presides_in_absence: 'Presides when the presiding officer is absent',
    signs_minutes: 'Signs the minutes',
    keeps_minute_book: 'Keeps the minute book',
    gives_notice: 'Sends meeting notices and the board packet',
    presents_financial_report: 'Presents the financial report',
  } as Readonly<Record<string, string>>,

  // ── Governance › Officers, "What each title does in Givmo" ─────────────────────────────────────

  // Drawn, l3-duties-start and l3-duties (the card's heading).
  dutiesTitle: 'What each title does in Givmo',
  // Drawn, l3-duties-start (the line before an Owner adopts them).
  dutiesStart: 'Givmo’s starting point — change it to match your bylaws',
  // Drawn, l3-duties ("Adopted by Maya Solberg on Oct 5, 2026").
  dutiesAdopted: (name: string, date: string) => `Adopted by ${name} on ${date}`,
  // Not drawn: an adoption whose adopter's name doesn't resolve.
  dutiesAdoptedOn: (date: string) => `Adopted on ${date}`,
  // Drawn, l3-duties-start (the first adoption).
  useThese: 'Use these as our board’s duties',
  // Drawn, l3-duties-start and l3-duties.
  change: 'Change',
  // Drawn, l3-duties-start and l3-duties (the last group).
  notAssigned: 'Not assigned to a title',
  // Drawn, l3-duties ("From your governing documents: Bylaws, Art. VI
  // §6"), and l3-member-duties.
  sourceCited: (citation: string) => `From your governing documents: ${citation}`,
  // Drawn by the frames' build for an adopted duty with no citation;
  // no frame shows that state.
  sourceStart: 'Givmo’s starting point',
  // Drawn, l3-duties-start and l3-duties ("How this works", its three
  // lines). The second changed in CBG-FIX104 (the copy page's row 2.1): the roster now holds
  // committee members who aren't on the board, who can't sign the board's minutes.
  dutiesHow: [
    'Nothing here applies until your board uses these as its duties.',
    'They decide whom Board asks to sign the minutes, and what a few dialogs suggest. They never decide who may act: anyone serving on the board can still sign, and any officer with access can still schedule a meeting or send a packet.',
    'Givmo doesn’t read your governing documents. You can note where each duty is written.',
  ],
  // Drawn, l3-duties ("Show 1 earlier version").
  earlierVersions: (n: number) => `Show ${n} earlier ${n === 1 ? 'version' : 'versions'}`,
  // Not drawn: the open history's toggle back.
  hideEarlierVersions: 'Hide earlier versions',
  // Not drawn: one superseded version's heading in the open history.
  earlierVersion: (adopted: string, replaced: string) => `In force ${adopted} – ${replaced}`,
  // Not drawn: an earlier version that assigned nothing.
  earlierNone: 'No duties were assigned.',
  // Not drawn: the first adoption, done; a change, saved; a write that
  // failed with no reason served; the duties couldn't be read.
  dutiesAdoptedToast: 'Your board’s duties are in use.',
  dutiesSavedToast: 'Duties saved.',
  dutiesFailed: 'We couldn’t save your board’s duties. Please try again.',
  dutiesLoadFailed: 'We couldn’t load what each title does in Givmo.',

  // ── "Change": the duties dialog ────────────────────────────────────────────────────────────────

  // Drawn, l3-duties-edit.
  dutiesEditorTitle: 'Your board’s duties',
  dutiesEditorLead:
    'Choose which title does each of these in Givmo. Saving replaces the duties in force from now on; the earlier version stays in the history.',
  dutyTitleField: 'Title',
  noTitle: 'No title',
  dutyCitationField: 'Where it’s written (optional)',
  save: 'Save',

  // ── The member page ────────────────────────────────────────────────────────────────────────────

  // Drawn, l3-member-duties (under each title the person holds).
  whatItDoes: 'What this title does in Givmo',

  // ── Whom Board asks to sign the minutes ────────────────────────────────────────────────────────

  // Drawn, l3-awaiting ("Awaiting the Secretary’s signature (Maren
  // Lindqvist)").
  awaitingSignature: (title: string, name: string) => `Awaiting the ${title}’s signature (${name})`,
  // Drawn, l3-onbehalf-suggested.
  onBehalfSuggested: (title: string) =>
    `Suggested by your board’s duties: the ${title} signs the minutes.`,

  // ── The holdover (CBG-YR3-ROLES-HOLDOVER) ──────────────────────────────────────────────────────

  // Not drawn — Jonathan's own words ("term ended – still serving",
  // 2026-10-12 14:10 PDT), beside a holder on the Officers page whose recorded term end has passed
  // while the title is still theirs, so the Owner can act. Drawn for his eye on the copy page.
  termEndedStillServing: (date: string) => `Term ended ${date} – still serving`,
  // Not drawn: the same, when two people hold the title, so each line
  // says whose term it is.
  termEndedStillServingFor: (name: string, date: string) =>
    `${name} · Term ended ${date} – still serving`,
  // Approved (CBG-YR3-ROLES-HOLDOVER-PAGE, Jonathan 2026-10-13 12:09 PDT): the member's own page,
  // beside a title still held past its recorded term end — "Since Oct 2, 2024 · Term ended Jun 30,
  // 2026 – still serving", and with no start recorded "Term ended Jun 30, 2026 – still serving".
  heldOverDates: (since: string | null, ended: string) =>
    since === null
      ? `Term ended ${ended} – still serving`
      : `Since ${since} · Term ended ${ended} – still serving`,
} as const;
